import { inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FuseConfirmationConfig } from '@fuse/services/confirmation/confirmation.types';
import { FuseConfirmationDialogComponent } from '@fuse/services/confirmation/dialog/dialog.component';
import { merge } from 'lodash-es';

@Injectable({providedIn: 'root'})
export class FuseConfirmationService
{
    private static _instanceCount = 0;

    private _matDialog: MatDialog = inject(MatDialog);
    private _defaultConfig: FuseConfirmationConfig = {
        title      : 'Confirm action',
        message    : 'Are you sure you want to confirm this action?',
        icon       : {
            show : true,
            name : 'heroicons_outline:exclamation-triangle',
            color: 'warn',
        },
        actions    : {
            confirm: {
                show : true,
                label: 'Confirm',
                color: 'warn',
            },
            cancel : {
                show : true,
                label: 'Cancel',
            },
        },
        dismissible: false,
    };

    /**
     * Constructor
     */
    constructor()
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    open(config: FuseConfirmationConfig = {}): MatDialogRef<FuseConfirmationDialogComponent>
    {
        // SAI PATCH: unique ids per dialog so two confirmations opened in the
        // same session never share an id, which would break the aria wiring.
        const instanceId = ++FuseConfirmationService._instanceCount;

        // Merge the user config with the default config
        const userConfig = merge({}, this._defaultConfig, config, {
            titleId  : `fuse-confirmation-title-${instanceId}`,
            messageId: `fuse-confirmation-message-${instanceId}`,
        });

        // Open the dialog
        return this._matDialog.open(FuseConfirmationDialogComponent, {
            // SAI PATCH: focus the dialog itself rather than nothing. `false`
            // left focus behind the overlay, so the question was never
            // announced and Tab kept walking the page underneath. The confirm
            // button is deliberately not auto-focused: many of these dialogs
            // confirm a delete, and Enter should not be able to fire it blind.
            autoFocus      : 'dialog',
            disableClose   : !userConfig.dismissible,
            data           : userConfig,
            ariaLabelledBy : userConfig.title ? userConfig.titleId : null,
            ariaDescribedBy: userConfig.message ? userConfig.messageId : null,
            // SAI PATCH: width from the shared dialog-size token, not w-128.
            panelClass     : ['fuse-confirmation-dialog-panel', 'sai-dialog-sm'],
        });
    }
}
